import styled from 'styled-components';
import React, { useContext, useEffect } from 'react';
import { LabelSelector, ListWrapper, Title, TitleHint } from './components';
import SelectInput from '../components/SelectInput';
import { EditorContext } from '../../../utils/contexts';
import { TextField } from '@mui/material';
import ContactListsInput from '../components/ContactListsInput';
import { withEditorPageTracking } from '../../../shared/Tracking';
import { days, extractPlanTier } from '../utils';
import useAPI from '../../../utils/api';
import useFetch from '../../../utils/useFetch';
import PaywallLock from '../../PaywallLock';

function FormConversionStep({ className }) {
  const api = useAPI();
  const { settings, updateSettings, systemUpdate } = useContext(EditorContext);
  const { data: fetchedContactLists } = useFetch(
    !settings.site?.contact_lists ? 'contactLists' : null
  );
  const contactLists = settings.site?.contact_lists || fetchedContactLists || [];
  const siteId = api.siteId;
  const planTier = extractPlanTier(api.plan);

  const afterSubmitOptions = [
    { value: 0, label: 'Show default thank you message' },
    { value: 1, label: 'Show a custom message' },
    { value: 2, label: 'Redirect to a URL' },
  ];

  const updateAfterSubmit = (value) => {
    const customSettings = { ...settings.settings };
    customSettings.after_email_submit_action = value;
    if (value === 1 && !settings.thank_you_text) {
      updateSettings({
        settings: customSettings,
        thank_you_text: 'Thank you for submitting!',
      });
    } else {
      updateSettings({ settings: customSettings });
    }
  };

  const updateRedirectURL = (event) => {
    const customSettings = { ...settings.settings };
    customSettings.redirect_url = event.target.value;
    updateSettings({ settings: customSettings });
  };

  const updateHideDuration = (value) => {
    const customSettings = { ...settings.settings };
    customSettings.cookie_settings = { ...customSettings.cookie_settings, success_duration: value };
    updateSettings({ settings: customSettings });
  };

  const updateContactList = (value) => {
    updateSettings({ contact_list_id: value });
  };

  const updateSuccessScript = (event) => {
    updateSettings({ success_script: event.target.value });
  };

  const isCustomMessage = settings.settings?.after_email_submit_action === 1;
  const isRedirect = settings.settings?.after_email_submit_action === 2;

  if (settings.preview_page !== 'conversion') {
    systemUpdate({ preview_page: 'conversion' });
  }

  if (settings.show_thankyou !== true) {
    systemUpdate({ show_thankyou: true });
  }

  useEffect(() => {
    if (window.hellobar) {
      window.hellobar.displayPage('thank-you');
      const handler = () => window.hellobar.displayPage('thank-you');
      window.hellobar.addEventListener('show', handler);
      return () => window.hellobar.removeEventListener('show', handler);
    }
  }, [window.hellobar]);

  return (
    <div className={className}>
      <div>
        <Title>Success Page</Title>
        <TitleHint>Customize what visitors see after submitting your form.</TitleHint>
      </div>
      <ListWrapper>
        <LabelSelector>
          <span>After a visitor submits the form, what should happen?</span>
          <SelectInput
            items={afterSubmitOptions}
            value={settings.settings?.after_email_submit_action ?? 0}
            onChange={updateAfterSubmit}
          />
        </LabelSelector>

        {isCustomMessage && (
          <LabelSelector>
            <span>Custom thank you message</span>
            <TextField
              variant="outlined"
              multiline
              rows={3}
              label="Thank You Message"
              value={settings.thank_you_text || ''}
              onChange={(e) => updateSettings({ thank_you_text: e.target.value })}
              size="small"
              fullWidth
            />
          </LabelSelector>
        )}

        {isRedirect && (
          <LabelSelector>
            <TextField
              variant="outlined"
              label="Redirect URL"
              value={settings.settings?.redirect_url || ''}
              onChange={updateRedirectURL}
              placeholder="https://example.com"
              size="small"
              fullWidth
            />
          </LabelSelector>
        )}

        <LabelSelector>
          <span>After submission, hide this form from the visitor for:</span>
          <SelectInput
            items={days}
            value={settings.settings?.cookie_settings?.success_duration ?? 0}
            onChange={updateHideDuration}
          />
        </LabelSelector>

        <LabelSelector>
          <span>After Hello Bar collects a form submission, where should we send it?</span>
          <ContactListsInput
            items={contactLists}
            value={settings.contact_list_id}
            onChange={updateContactList}
          />
        </LabelSelector>

        <LabelSelector>
          <span>
            After your form converts, run a script to fire an advanced pixel
            like Facebook's tracking pixel and more.
          </span>
          <TextField
            variant="outlined"
            multiline
            rows={3}
            label="Conversion Triggered Script"
            value={settings.success_script || ''}
            onChange={updateSuccessScript}
            placeholder="<script></script>"
            size="small"
            disabled={planTier < 2}
          />
          {planTier < 2 && (
            <PaywallLock
              message="Not available on your plan. Please upgrade your subscription."
              url={siteId ? `/sites/${siteId}/upgrade` : ''}
            />
          )}
        </LabelSelector>
      </ListWrapper>
    </div>
  );
}

export default styled(withEditorPageTracking(FormConversionStep, 'FormConversion'))``;
